import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useCart, useStore } from '../lib/store'
import { SOCIALS } from '../lib/site'
import { api, ApiError } from '../lib/api'

/** Renders a photograph, or the design's placeholder treatment when there is none. */
export function Img({ src, alt, label }: { src?: string; alt: string; label?: string }) {
  if (!src) return <div className="ph">{label ?? alt}</div>
  return <img src={src} alt={alt} loading="lazy" />
}

export function Fill({ src, alt, label }: { src?: string; alt: string; label?: string }) {
  return (
    <span className="fill">
      <Img src={src} alt={alt} label={label} />
    </span>
  )
}

const NAV = [
  { to: '/shop', label: 'Shop' },
  { to: '/shop?filter=Bestsellers', label: 'Bestsellers' },
  { to: '/shop#concern', label: 'Skincare' },
  { to: '/quiz', label: 'Quiz' },
  { to: '/journal', label: 'Journal' },
  { to: '/about', label: 'About' },
]

function Icon({ d, label }: { d: string; label: string }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" aria-label={label} role="img">
      <path d={d} />
    </svg>
  )
}

export function Nav() {
  const { settings, user } = useStore()
  const { count } = useCart()
  const { pathname } = useLocation()

  const threshold = settings?.freeShippingThreshold ?? 50
  const symbol = settings?.currencySymbol ?? '£'

  return (
    <div style={{ fontFamily: 'var(--sans)', color: 'var(--ink)' }}>
      {settings?.announcementBar !== false && (
        <div
          style={{
            background: 'var(--ink)', color: '#f6efe9', fontSize: 10.5, letterSpacing: '.2em',
            textTransform: 'uppercase', textAlign: 'center', padding: '9px 16px',
          }}
        >
          {settings?.announcementText || 'Complimentary delivery on orders over '}
          {symbol}
          {threshold}
        </div>
      )}

      <header
        style={{
          display: 'flex', alignItems: 'center', gap: 24, padding: '18px clamp(16px,3vw,44px)',
          borderBottom: '1px solid var(--line)', background: 'var(--bg)', flexWrap: 'wrap',
        }}
      >
        <nav style={{ display: 'flex', gap: 'clamp(12px,1.8vw,26px)', flex: '1 1 0', minWidth: 0, flexWrap: 'wrap', alignItems: 'center' }}>
          {NAV.map((item) => {
            const base = item.to.split(/[?#]/)[0]
            const active = pathname === base
            return (
              <span key={item.label} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Link to={item.to} style={{ fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase' }}>
                  {item.label}
                </Link>
                {active && <span style={{ height: 1, background: 'var(--rose)' }} />}
              </span>
            )
          })}
        </nav>

        <Link to="/" style={{ position: 'relative', flex: '0 0 auto', display: 'block', padding: '4px 10px' }} aria-label="Lumera home">
          <span style={{ position: 'absolute', right: 34, top: -2, width: 34, height: 34, borderRadius: '50%', background: 'var(--blush-2)', zIndex: 0 }} />
          <span style={{ position: 'relative', zIndex: 1, fontFamily: 'var(--serif)', fontSize: 'clamp(22px,2.4vw,30px)', fontWeight: 400, letterSpacing: '.3em' }}>
            LUMERA
          </span>
        </Link>

        <div style={{ flex: '1 1 0', display: 'flex', justifyContent: 'flex-end', gap: 'clamp(10px,1.4vw,20px)', alignItems: 'center' }}>
          <Link to="/shop" aria-label="Search products" title="Search">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
              <circle cx="11" cy="11" r="7" />
              <path d="M16.5 16.5 21 21" />
            </svg>
          </Link>
          <Link to={user ? '/account' : '/login'} aria-label={user ? 'Your account' : 'Sign in'} title={user ? 'Account' : 'Sign in'}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
              <circle cx="12" cy="8.5" r="3.5" />
              <path d="M5 20c1.6-3.4 4-5 7-5s5.4 1.6 7 5" />
            </svg>
          </Link>
          <Link to="/cart" aria-label={`Your bag, ${count} item${count === 1 ? '' : 's'}`} title="Bag" style={{ position: 'relative' }}>
            <Icon d="M5.5 8h13l-1 12h-11l-1-12ZM9 8V6.5a3 3 0 0 1 6 0V8" label="Bag" />
            {count > 0 && (
              <span
                style={{
                  position: 'absolute', top: -6, right: -8, minWidth: 16, height: 16, padding: '0 4px',
                  borderRadius: 8, background: 'var(--rose)', color: '#fff', fontSize: 9.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', letterSpacing: 0,
                }}
              >
                {count}
              </span>
            )}
          </Link>
        </div>
      </header>
    </div>
  )
}

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  TikTok: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 3h2.6c.3 2 1.5 3.3 3.4 3.5V9c-1.3.1-2.5-.3-3.5-1v6.4A5.4 5.4 0 1 1 11 9v2.7a2.7 2.7 0 1 0 3 2.7V3Z" />
    </svg>
  ),
  Instagram: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="4" y="4" width="16" height="16" rx="4.5" />
      <circle cx="12" cy="12" r="3.4" />
      <circle cx="16.8" cy="7.2" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  Facebook: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="4" y="4" width="16" height="16" rx="4.5" />
      <path d="M14.8 8.8h-1.1a1.1 1.1 0 0 0-1.1 1.1v9.5" />
      <path d="M10.6 12.7h4.2" />
    </svg>
  ),
}

export function Footer() {
  const { settings } = useStore()
  const [email, setEmail] = useState('')
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)

  async function subscribe(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setMsg('')
    try {
      const r = await api.subscribe(email)
      setMsg(r.message)
      setEmail('')
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Could not subscribe just now.')
    } finally {
      setBusy(false)
    }
  }

  const col: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10 }
  const head: React.CSSProperties = { margin: '0 0 4px', fontSize: 10, fontWeight: 500, letterSpacing: '.2em', textTransform: 'uppercase' }
  const link: React.CSSProperties = { fontSize: 11.5, fontWeight: 300, color: 'var(--muted)' }

  return (
    <footer style={{ background: 'var(--oat)', padding: 'clamp(32px,4vw,56px) clamp(16px,3vw,44px) 0', color: 'var(--ink)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 'clamp(20px,2.6vw,40px)', paddingBottom: 'clamp(28px,3vw,44px)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '.3em', color: '#a8968b' }}>LUMERA</div>
          <p style={{ margin: 0, fontSize: 11.5, fontWeight: 300, lineHeight: 1.8, color: 'var(--muted)', maxWidth: '28ch' }}>
            Advanced skincare for modern skin. Powered by science. Inspired by you.
          </p>
          <div className="social-row">
            {SOCIALS.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer noopener" aria-label={`${s.label} — ${s.handle}`}>
                {SOCIAL_ICONS[s.label]}
              </a>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h3 style={head}>Join the Lumera world</h3>
          <p style={{ margin: 0, fontSize: 11.5, fontWeight: 300, lineHeight: 1.8, color: 'var(--muted)', maxWidth: '30ch' }}>
            Be the first to know about new launches, exclusive offers and skincare advice.
          </p>
          <form onSubmit={subscribe} style={{ display: 'flex', border: '1px solid var(--line-2)', background: 'var(--bg)', maxWidth: 300 }}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              aria-label="Email address"
              style={{ flex: '1 1 auto', minWidth: 0, border: 'none', background: 'none', padding: '11px 13px', fontFamily: 'var(--sans)', fontSize: 11.5, color: 'var(--ink)', outline: 'none' }}
            />
            <button type="submit" disabled={busy} aria-label="Subscribe" style={{ border: 'none', background: 'var(--ink)', color: 'var(--bg)', width: 36, cursor: 'pointer', fontSize: 12 }}>
              →
            </button>
          </form>
          <div style={{ fontSize: 11, color: '#8a6a6a', minHeight: '1.4em' }}>{msg}</div>
          <p style={{ margin: 0, fontSize: 10, lineHeight: 1.7, color: 'var(--muted-2)', maxWidth: '32ch' }}>
            You can unsubscribe at any time. See our{' '}
            <Link to="/privacy" style={{ textDecoration: 'underline' }}>Privacy Policy</Link>.
          </p>
        </div>

        <div style={col}>
          <h3 style={head}>Shop</h3>
          <Link to="/shop" style={link}>All Products</Link>
          <Link to="/shop?filter=Bestsellers" style={link}>Bestsellers</Link>
          <Link to="/shop#concern" style={link}>Shop by Concern</Link>
          <Link to="/quiz" style={link}>Skin Quiz</Link>
        </div>

        <div style={col}>
          <h3 style={head}>About</h3>
          <Link to="/about#philosophy" style={link}>Our Philosophy</Link>
          <Link to="/about" style={link}>Our Story</Link>
          <Link to="/journal" style={link}>The Journal</Link>
          <Link to="/journal#faqs" style={link}>FAQs</Link>
        </div>

        <div style={col}>
          <h3 style={head}>Support</h3>
          <Link to="/track" style={link}>Track Order</Link>
          <Link to="/journal#faqs" style={link}>Delivery</Link>
          <Link to="/journal#faqs" style={link}>Returns</Link>
          <Link to="/account" style={link}>Your Account</Link>
          <Link to="/terms" style={link}>Terms &amp; Conditions</Link>
          <Link to="/privacy" style={link}>Privacy Policy</Link>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #ddd5cd', display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', padding: '16px 0 22px', fontSize: 10.5, color: 'var(--muted-2)' }}>
        <span>© {new Date().getFullYear()} {settings?.storeName ?? 'Lumera'} Beauty. All rights reserved.</span>
        <span>
          {settings?.countryLabel ?? 'United Kingdom'} ({settings?.currencySymbol ?? '£'})
        </span>
      </div>
    </footer>
  )
}

export function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="shell">
      <Nav />
      {children}
      <Footer />
    </div>
  )
}

export function Stars({ rating, count }: { rating: number | null; count?: number }) {
  const filled = Math.round(rating ?? 5)
  return (
    <div className="stars">
      <span aria-label={`${filled} out of 5 stars`} style={{ color: 'var(--rose-soft)', letterSpacing: '.18em' }}>
        {'★'.repeat(filled)}
        {'☆'.repeat(Math.max(0, 5 - filled))}
      </span>
      {count !== undefined && count > 0 && <span>({count})</span>}
    </div>
  )
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="section" style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase' }}>
      {label}…
    </div>
  )
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <div className="notice notice-error">{message}</div>
      {onRetry && (
        <button className="btn btn-sm" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  )
}

/** Shared product tile, matching the card treatment across Home and Shop. */
export function ProductCard({ product, onAdd }: { product: import('../lib/api').Product; onAdd?: (slug: string) => void }) {
  const { money } = useStore()
  return (
    <article className="card">
      <Link to={`/product/${product.slug}`} className="card-media" style={{ display: 'block' }}>
        <Img src={product.image} alt={product.name} label={product.name} />
        {product.badge && product.inStock && <span className="badge">{product.badge}</span>}
        {!product.inStock && <span className="badge badge-out">Sold out</span>}
      </Link>
      <div className="card-body">
        {product.step && <div className="card-step">{product.step}</div>}
        <h3 className="card-name">
          <Link to={`/product/${product.slug}`}>{product.name}</Link>
        </h3>
        <p className="card-blurb">{product.tagline}</p>
        {product.reviewCount > 0 && <Stars rating={product.rating} count={product.reviewCount} />}
        <div className="card-price">
          {money(product.price)}
          {product.size && ` · ${product.size}`}
        </div>
        {onAdd && (
          <button
            className="btn btn-sm"
            style={{ marginTop: 6, width: '100%' }}
            disabled={!product.inStock}
            onClick={() => onAdd(product.slug)}
          >
            {product.inStock ? 'Add to bag' : 'Sold out'}
          </button>
        )}
      </div>
    </article>
  )
}
